import React from "react";
import ReactDOM from "react-dom/client";
/* ── Self-hosted brand fonts (60db system): Newsreader display, IBM Plex Sans/Mono ── */
import "@fontsource-variable/newsreader";
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/500.css";
import "./index.css";
import { ChakraProvider, extendTheme, useToast } from "@chakra-ui/react";
import App from "./App";
import { AppConfigProvider } from "AppConfigProvider";
import { initToast } from "utils/toastHelper";
import { syncThemeModeAtBoot } from "utils/theme-mode-boot";
import initializeI18n from "./i18nInit";
import "react-vertical-timeline-component/style.min.css";
import "react-quill/dist/quill.snow.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "@livekit/components-styles";
import TagManager from 'react-gtm-module';

const tagManagerArgs = {
    gtmId: 'GTM-WPN2VBML',
    dataLayer: {
      server_container_url: 'https://gtm.qcall.ai'
    }
}

/* ══════════════════════════════════════════════════════════════════════════════
   60db / QCall Theme — Chakra UI Configuration
   Warm paper, one flame accent, rules & edges. Light hexes only — dark mode
   comes from the colorMode ternaries below plus the CSS vars in index.css.
   ══════════════════════════════════════════════════════════════════════════════ */
/* Resolve dark mode ONCE before Chakra mounts so `themeMode`/body class
   (Tailwind) and `chakra-ui-color-mode` (Chakra) can never hydrate apart.
   Seeding initialColorMode from it makes Chakra's very FIRST render match —
   without this, Chakra always renders "light" first and only syncs to the
   stored mode in a mount effect, which is what broke dark mode on refresh. */
const bootThemeMode = syncThemeModeAtBoot();

const theme = extendTheme({
  config: {
    initialColorMode: bootThemeMode,
    useSystemColorMode: false,
  },

  /* ── Brand Fonts ── */
  fonts: {
    heading: "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, sans-serif",
    body: "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, sans-serif",
    mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
    fontDisplay: "'Newsreader Variable', Georgia, 'Times New Roman', serif",
  },

  /* ── Brand Colors (light hexes; retired hues resolve to the flame) ── */
  colors: {
    /* white/black literals softened to the sheet tone and ink (parity with
       tailwind.config.js) so bg="white" call sites cannot render pure #fff */
    white: "#f7f3ec",
    black: "#2f2b27",
    /* Warm paper/ink neutrals */
    gray: {
      50: "#eae4d9",
      100: "#e2dbcd",
      200: "#d5ccbc",
      300: "#c4b9a6",
      400: "#7d7669",
      500: "#706a5f",
      600: "#655f57",
      700: "#55504a",
      800: "#3a352f",
      900: "#2f2b27",
    },
    /* Flame — the single accent (under every retired hue name) */
    purple: {
      50: "#fbf0ea",
      100: "#f6e1d6",
      200: "#b8451f",
      300: "#c74e27",
      400: "#eb663a",
      500: "#b8451f",
      600: "#9a3818",
      700: "#822f14",
      800: "#6b2610",
      900: "#541e0d",
    },
    /* Danger */
    red: {
      50: "#f8efe9",
      100: "#f0dbd0",
      200: "#e2b6a2",
      300: "#d18e71",
      400: "#c26444",
      500: "#b23a1b",
      600: "#933016",
      700: "#752712",
      800: "#581d0d",
      900: "#3d1409",
    },
    /* Orange → retired to the flame */
    orange: {
      50: "#fbf0ea",
      100: "#f6e1d6",
      200: "#b8451f",
      300: "#c74e27",
      400: "#eb663a",
      500: "#b8451f",
      600: "#9a3818",
      700: "#822f14",
      800: "#6b2610",
      900: "#541e0d",
    },
    /* Success */
    green: {
      50: "#eae4d9",
      100: "#d8e8dd",
      200: "#aed2ba",
      300: "#7db694",
      400: "#528f6c",
      500: "#2f6b46",
      600: "#275c3b",
      700: "#204b31",
      800: "#183b25",
      900: "#122d1c",
    },
    /* Info */
    blue: {
      50: "#eae4d9",
      100: "#d6e3eb",
      200: "#adc6d7",
      300: "#7ea3bd",
      400: "#55829f",
      500: "#35566e",
      600: "#2d4a5e",
      700: "#253d4f",
      800: "#1d313f",
      900: "#16242f",
    },
    /* Teal → success */
    teal: {
      50: "#eae4d9",
      100: "#d8e8dd",
      200: "#aed2ba",
      300: "#7db694",
      400: "#528f6c",
      500: "#2f6b46",
      600: "#275c3b",
      700: "#204b31",
      800: "#183b25",
      900: "#122d1c",
    },
    /* Cyan → info */
    cyan: {
      50: "#eae4d9",
      100: "#d6e3eb",
      200: "#adc6d7",
      300: "#7ea3bd",
      400: "#55829f",
      500: "#35566e",
      600: "#2d4a5e",
      700: "#253d4f",
      800: "#1d313f",
      900: "#16242f",
    },
    /* Warning */
    yellow: {
      50: "#eae4d9",
      100: "#eee1c6",
      200: "#dcc492",
      300: "#c8a35d",
      400: "#a97d33",
      500: "#8a5a12",
      600: "#744c10",
      700: "#5e3e0d",
      800: "#49310b",
      900: "#332307",
    },
    /* Brand / Primary → flame */
    brand: {
      50: "#fbf0ea",
      100: "#f6e1d6",
      200: "#b8451f",
      300: "#c74e27",
      400: "#eb663a",
      500: "#b8451f",
      600: "#9a3818",
      700: "#822f14",
      800: "#6b2610",
      900: "#541e0d",
    },
  },

  /* ── Brand Typography Styles ── */
  textStyles: {
    /* Display roles: Newsreader 400 — size carries the emphasis, not weight */
    display: {
      fontSize: "3rem",
      fontWeight: "400",
      lineHeight: "1.1",
      letterSpacing: "-0.02em",
      fontFamily: "'Newsreader Variable', Georgia, 'Times New Roman', serif",
    },
    h1: {
      fontSize: "2.25rem",
      fontWeight: "400",
      lineHeight: "1.2",
      letterSpacing: "-0.02em",
      fontFamily: "'Newsreader Variable', Georgia, 'Times New Roman', serif",
    },
    h2: {
      fontSize: "1.875rem",
      fontWeight: "400",
      lineHeight: "1.25",
      letterSpacing: "-0.01em",
      fontFamily: "'Newsreader Variable', Georgia, 'Times New Roman', serif",
    },
    h3: {
      fontSize: "1.5rem",
      fontWeight: "400",
      lineHeight: "1.3",
      letterSpacing: "-0.01em",
      fontFamily: "'Newsreader Variable', Georgia, 'Times New Roman', serif",
    },
    h4: {
      fontSize: "1.25rem",
      fontWeight: "500",
      lineHeight: "1.35",
      fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: "500",
      lineHeight: "1.4",
      fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
    },
    h6: {
      fontSize: "1rem",
      fontWeight: "500",
      lineHeight: "1.4",
      fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
    },
    "body-lg": {
      fontSize: "1.125rem",
      fontWeight: "400",
      lineHeight: "1.62",
    },
    body: {
      fontSize: "1rem",
      fontWeight: "400",
      lineHeight: "1.62",
    },
    "body-sm": {
      fontSize: "0.875rem",
      fontWeight: "400",
      lineHeight: "1.55",
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: "400",
      lineHeight: "1.5",
      letterSpacing: "0.02em",
    },
    overline: {
      fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: "11.5px",
      fontWeight: "500",
      lineHeight: "1.5",
      letterSpacing: "0.07em",
      textTransform: "uppercase" as const,
    },
  },

  /* ── Brand Radius: one radius, 2px ── */
  radii: {
    none: "0",
    sm: "2px",
    base: "2px",
    md: "2px",
    lg: "2px",
    xl: "2px",
    "2xl": "2px",
    full: "999px",
  },

  /* ── Brand Shadows: none, except the flame focus glow ── */
  shadows: {
    xs: "none",
    sm: "none",
    base: "none",
    md: "none",
    lg: "none",
    xl: "none",
    "2xl": "none",
    outline: "0 0 0 3px rgba(235, 102, 58, 0.16)",
  },

  /* ── Brand Borders: a rule and an edge ── */
  borders: {
    none: "none",
    "1px": "1px solid #d5ccbc",
    "2px": "2px solid #2f2b27",
  },

  /* ── Brand Spacing (4px base) ── */
  space: {
    "0.5": "2px",
    "1": "4px",
    "1.5": "6px",
    "2": "8px",
    "3": "12px",
    "4": "16px",
    "5": "20px",
    "6": "24px",
    "8": "32px",
    "10": "40px",
    "12": "48px",
    "16": "64px",
    "20": "80px",
    "24": "96px",
  },

  /* ── Brand Z-Index ── */
  zIndices: {
    overlay: 1200,
    modal: 1300,
    popover: 1400,
    toast: 1500,
    tooltip: 1600,
  },

  /* ── Component Styles ── */
  components: {
    /* ── Button: a mono control; colour changes, never lift ── */
    Button: {
      baseStyle: {
        fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontWeight: "500",
        borderRadius: "2px",
        letterSpacing: "0.02em",
        transition: "background 120ms cubic-bezier(0.2, 0.8, 0.2, 1), border-color 120ms cubic-bezier(0.2, 0.8, 0.2, 1), color 120ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        _focus: {
          boxShadow: "none",
          outline: "2px solid transparent",
        },
        _focusVisible: {
          boxShadow: "none",
          outline: "2px solid var(--qc-bx, #b8451f)",
          outlineOffset: "3px",
        },
      },
      sizes: {
        xs: {
          h: "28px",
          fontSize: "12px",
          px: "8px",
        },
        sm: {
          h: "34px",
          fontSize: "12px",
          px: "12px",
          letterSpacing: "0.03em",
        },
        md: {
          h: "44px",
          fontSize: "13.5px",
          px: "16px",
        },
        lg: {
          h: "46px",
          fontSize: "13.5px",
          px: "20px",
        },
        xl: {
          h: "50px",
          fontSize: "13.5px",
          px: "24px",
        },
      },
      variants: {
        solid: {
          bg: "#b8451f",
          color: "#f7f3ec",
          _hover: { bg: "#9a3818" },
          _active: { bg: "#822f14" },
          _disabled: {
            bg: "#e2dbcd",
            color: "#7d7669",
            opacity: 1,
          },
        },
        outline: {
          bg: "#f7f3ec",
          color: "#3a352f",
          borderColor: "#8a8175",
          borderWidth: "1px",
          _hover: {
            bg: "#eae4d9",
            borderColor: "#8a8175",
          },
        },
        ghost: {
          color: "#55504a",
          _hover: {
            bg: "#e2dbcd",
            color: "#3a352f",
          },
        },
        danger: {
          bg: "#b23a1b",
          color: "#f7f3ec",
          _hover: { bg: "#933016" },
        },
      },
      defaultProps: {
        variant: "solid",
        size: "md",
      },
    },

    /* ── Input: a fresh sheet on an edge, flame focus ring ── */
    Input: {
      baseStyle: {
        field: {
          fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
          borderRadius: "2px",
          transition: "border-color 120ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 120ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        },
      },
      sizes: {
        sm: {
          field: {
            h: "32px",
            fontSize: "0.875rem",
            px: "10px",
            borderRadius: "2px",
          },
        },
        md: {
          field: {
            h: "40px",
            fontSize: "0.875rem",
            px: "12px",
            borderRadius: "2px",
          },
        },
        lg: {
          field: {
            h: "46px",
            fontSize: "1rem",
            px: "16px",
            borderRadius: "2px",
          },
        },
      },
      variants: {
        outline: (props: any) => ({
          field: {
            bg: props.colorMode === "dark" ? "#26231f" : "#f7f3ec",
            borderColor: props.colorMode === "dark" ? "#b8afa2" : "#8a8175",
            color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
            _hover: { borderColor: props.colorMode === "dark" ? "#c4b9a6" : "#55504a" },
            _focus: {
              borderColor: props.colorMode === "dark" ? "#b8afa2" : "#8a8175",
              boxShadow: "var(--shadow-glow, 0 0 0 3px rgba(235, 102, 58, 0.16))",
            },
            _invalid: {
              borderColor: "#b23a1b",
              borderWidth: "1px",
            },
            _disabled: {
              bg: props.colorMode === "dark" ? "#2e2a26" : "#eae4d9",
              color: "#7d7669",
              borderColor: props.colorMode === "dark" ? "#38332e" : "#d5ccbc",
              opacity: 1,
            },
            _placeholder: {
              color: "#706a5f",
            },
          },
        }),
      },
      defaultProps: {
        variant: "outline",
        size: "md",
      },
    },

    /* ── Textarea ── */
    Textarea: {
      baseStyle: {
        fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
        borderRadius: "2px",
        transition: "border-color 120ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 120ms cubic-bezier(0.2, 0.8, 0.2, 1)",
      },
      variants: {
        outline: (props: any) => ({
          bg: props.colorMode === "dark" ? "#26231f" : "#f7f3ec",
          borderColor: props.colorMode === "dark" ? "#b8afa2" : "#8a8175",
          color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
          _hover: { borderColor: props.colorMode === "dark" ? "#c4b9a6" : "#55504a" },
          _focus: {
            borderColor: props.colorMode === "dark" ? "#b8afa2" : "#8a8175",
            boxShadow: "var(--shadow-glow, 0 0 0 3px rgba(235, 102, 58, 0.16))",
          },
          _placeholder: {
            color: "#706a5f",
          },
        }),
      },
    },

    /* ── Select ── */
    Select: {
      baseStyle: {
        field: {
          fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
          borderRadius: "2px",
        },
      },
      variants: {
        outline: (props: any) => ({
          field: {
            bg: props.colorMode === "dark" ? "#26231f" : "#f7f3ec",
            borderColor: props.colorMode === "dark" ? "#b8afa2" : "#8a8175",
            color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
            _hover: { borderColor: props.colorMode === "dark" ? "#c4b9a6" : "#55504a" },
            _focus: {
              borderColor: props.colorMode === "dark" ? "#b8afa2" : "#8a8175",
              boxShadow: "var(--shadow-glow, 0 0 0 3px rgba(235, 102, 58, 0.16))",
            },
          },
        }),
      },
    },

    /* ── Card: opaque paper, 1px rule, no shadow ── */
    Card: {
      baseStyle: (props: any) => ({
        container: {
          bg: props.colorMode === "dark" ? "#26231f" : "#f7f3ec",
          color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
          borderRadius: "2px",
          border: props.colorMode === "dark" ? "1px solid #38332e" : "1px solid #d5ccbc",
          boxShadow: "none",
          padding: "24px",
        },
      }),
    },

    /* ── Modal: paper enclosed by a rule, ink scrim ── */
    Modal: {
      baseStyle: (props: any) => ({
        dialog: {
          /* Responsive default: 12px side gutters below md so ModalContent without its
             own maxW never sits edge-to-edge on phones. Props on <ModalContent> (maxW/w/mx)
             and size="full" (sizes.full below) override this baseStyle, so they still win. */
          mx: { base: 3, md: "auto" },
          minW: 0,
          borderRadius: "2px",
          bg: props.colorMode === "dark" ? "#26231f" : "#f7f3ec",
          color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
          border: props.colorMode === "dark" ? "1px solid #38332e" : "1px solid #d5ccbc",
          boxShadow: "none",
        },
        header: {
          color: props.colorMode === "dark" ? "#f7f3ec" : "#2f2b27",
        },
        body: {
          color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
        },
        closeButton: {
          color: props.colorMode === "dark" ? "#c4b9a6" : "#55504a",
        },
        overlay: {
          bg: "rgba(28, 26, 24, 0.8)",
        },
      }),
      sizes: {
        sm: { dialog: { maxW: "min(400px, calc(100vw - 16px))" } },
        md: { dialog: { maxW: "min(560px, calc(100vw - 16px))" } },
        lg: { dialog: { maxW: "min(720px, calc(100vw - 16px))" } },
        full: { dialog: { maxW: "90vw" } },
      },
    },

    /* ── Tooltip: ink panel, paper type, mono ── */
    Tooltip: {
      baseStyle: {
        bg: "#3a352f",
        color: "#f7f3ec",
        fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "12px",
        fontWeight: "400",
        px: "10px",
        py: "6px",
        borderRadius: "2px",
        boxShadow: "none",
        maxW: "240px",
      },
    },

    /* ── Badge: a filed label or a stamped flame field, never a pill ── */
    Badge: {
      baseStyle: {
        fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "11.5px",
        fontWeight: "500",
        borderRadius: "2px",
        px: "8px",
        py: "2px",
        letterSpacing: "0.07em",
        textTransform: "uppercase",
      },
      variants: {
        subtle: {
          bg: "#e2dbcd",
          color: "#55504a",
        },
        solid: {
          bg: "#b8451f",
          color: "#f7f3ec",
        },
        outline: {
          borderColor: "#d5ccbc",
          color: "#55504a",
        },
      },
    },

    /* ── Table: mono filed headers, rows separate with a rule ── */
    Table: {
      baseStyle: (props: any) => ({
        th: {
          bg: props.colorMode === "dark" ? "#38332e" : "#e2dbcd",
          color: props.colorMode === "dark" ? "#b8afa2" : "#55504a",
          fontWeight: "500",
          fontSize: "11.5px",
          textTransform: "uppercase",
          letterSpacing: "0.07em",
          px: "12px",
          py: "16px",
          borderBottom: props.colorMode === "dark" ? "1px solid #38332e" : "1px solid #d5ccbc",
          fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        },
        td: {
          color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
          fontSize: "0.875rem",
          px: "12px",
          py: "16px",
          borderBottom: props.colorMode === "dark" ? "1px solid #38332e" : "1px solid #d5ccbc",
        },
        tr: {
          _hover: {
            bg: props.colorMode === "dark" ? "#2e2a26" : "#eae4d9",
          },
        },
      }),
    },

    /* ── Drawer ── */
    Drawer: {
      baseStyle: (props: any) => ({
        dialog: {
          bg: props.colorMode === "dark" ? "#26231f" : "#f7f3ec",
          color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
          borderLeft: props.colorMode === "dark" ? "1px solid #38332e" : "1px solid #d5ccbc",
          borderRadius: "2px 0 0 2px",
        },
        header: {
          color: props.colorMode === "dark" ? "#f7f3ec" : "#2f2b27",
          fontSize: "lg",
          fontWeight: "500",
        },
        body: {
          color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
        },
        closeButton: {
          color: props.colorMode === "dark" ? "#c4b9a6" : "#55504a",
        },
        overlay: {
          bg: "rgba(28, 26, 24, 0.8)",
        },
      }),
    },

    /* ── Menu ── */
    Menu: {
      baseStyle: (props: any) => ({
        list: {
          bg: props.colorMode === "dark" ? "#26231f" : "#f7f3ec",
          borderColor: props.colorMode === "dark" ? "#38332e" : "#d5ccbc",
          borderWidth: "1px",
          borderRadius: "2px",
          boxShadow: "none",
          py: "4px",
        },
        item: {
          fontSize: "0.875rem",
          color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
          px: "12px",
          py: "8px",
          borderRadius: "2px",
          mx: "4px",
          _hover: {
            bg: props.colorMode === "dark" ? "#2e2a26" : "#eae4d9",
          },
          _focus: {
            bg: props.colorMode === "dark" ? "#2e2a26" : "#eae4d9",
          },
        },
      }),
    },

    /* ── Popover ── */
    Popover: {
      baseStyle: (props: any) => ({
        content: {
          bg: props.colorMode === "dark" ? "#26231f" : "#f7f3ec",
          borderColor: props.colorMode === "dark" ? "#38332e" : "#d5ccbc",
          borderWidth: "1px",
          borderRadius: "2px",
          boxShadow: "none",
        },
      }),
    },

    /* ── Tabs: a row of mono controls; selected carries the flame ── */
    Tabs: {
      baseStyle: {
        tab: {
          fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          fontWeight: "500",
          fontSize: "13px",
          color: "#55504a",
          _selected: {
            color: "#b8451f",
            borderColor: "#b8451f",
          },
        },
      },
    },

    /* ── Slider ── */
    Slider: {
      baseStyle: {
        track: {
          bg: "#d5ccbc",
          borderRadius: "999px",
        },
        filledTrack: {
          bg: "#b8451f",
        },
        thumb: {
          bg: "#b8451f",
          borderColor: "#b8451f",
          _focus: {
            boxShadow: "0 0 0 3px rgba(235, 102, 58, 0.16)",
          },
        },
      },
    },

    /* ── Switch ── */
    Switch: {
      baseStyle: {
        track: {
          bg: "#7d7669", /* off-track needs real contrast on paper — surface tones vanish */
          _checked: {
            bg: "#b8451f",
          },
        },
      },
    },

    /* ── Checkbox ── */
    Checkbox: {
      baseStyle: {
        control: {
          borderColor: "#2f2b27",
          borderWidth: "2px",
          borderRadius: "2px",
          _checked: {
            bg: "#b8451f",
            borderColor: "#b8451f",
          },
          _focus: {
            boxShadow: "0 0 0 3px rgba(235, 102, 58, 0.16)",
          },
        },
        label: {
          fontSize: "0.875rem",
          color: "#3a352f",
        },
      },
    },

    /* ── Radio ── */
    Radio: {
      baseStyle: {
        control: {
          borderColor: "#2f2b27",
          borderWidth: "2px",
          _checked: {
            bg: "#b8451f",
            borderColor: "#b8451f",
          },
        },
        label: {
          fontSize: "0.875rem",
          color: "#3a352f",
        },
      },
    },

    /* ── Alert ── */
    Alert: {
      baseStyle: {
        container: {
          borderRadius: "2px",
          px: "16px",
          py: "12px",
        },
      },
      variants: {
        subtle: (props: any) => {
          const colorMap: Record<string, { bg: string; color: string }> = {
            success: { bg: "#eae4d9", color: "#204b31" },
            error: { bg: "#f8efe9", color: "#752712" },
            warning: { bg: "#eae4d9", color: "#5e3e0d" },
            info: { bg: "#eae4d9", color: "#253d4f" },
          };
          const colors = colorMap[props.status] || colorMap.info;
          return {
            container: { bg: colors.bg, color: colors.color },
          };
        },
      },
    },

    /* ── FormLabel: mono — the case that defines the rule ── */
    FormLabel: {
      baseStyle: (props: any) => ({
        fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "12px",
        fontWeight: "500",
        letterSpacing: "0.02em",
        color: props.colorMode === "dark" ? "#b8afa2" : "#55504a",
        mb: "1",
      }),
    },

    /* ── Form ── */
    Form: {
      baseStyle: (props: any) => ({
        helperText: {
          fontSize: "0.75rem",
          color: props.colorMode === "dark" ? "#9c9385" : "#706a5f",
        },
      }),
    },

    /* ── Heading: the display serif, weight 400 ── */
    Heading: {
      baseStyle: (props: any) => ({
        fontFamily: "'Newsreader Variable', Georgia, 'Times New Roman', serif",
        fontWeight: "400",
        color: props.colorMode === "dark" ? "#f7f3ec" : "#2f2b27",
      }),
      /* Size styles would re-impose semibold — pin them all to 400 */
      sizes: {
        "4xl": { fontWeight: "400" },
        "3xl": { fontWeight: "400" },
        "2xl": { fontWeight: "400" },
        xl: { fontWeight: "400" },
        lg: { fontWeight: "400" },
        md: { fontWeight: "400" },
        sm: { fontWeight: "400" },
        xs: { fontWeight: "400" },
      },
    },

    /* ── Text ── */
    Text: {
      baseStyle: (props: any) => ({
        fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
        color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
      }),
    },

    /* ── Divider: a rule ── */
    Divider: {
      baseStyle: (props: any) => ({
        borderColor: props.colorMode === "dark" ? "#38332e" : "#d5ccbc",
      }),
    },

    /* ── Skeleton ── */
    Skeleton: {
      baseStyle: {
        borderRadius: "2px",
        startColor: "#eae4d9",
        endColor: "#e2dbcd",
      },
    },

    /* ── Progress ── */
    Progress: {
      baseStyle: {
        track: {
          bg: "#e2dbcd",
          borderRadius: "999px",
        },
        filledTrack: {
          bg: "#b8451f",
          borderRadius: "999px",
        },
      },
    },

    /* ── Spinner ── */
    Spinner: {
      baseStyle: {
        color: "#b8451f",
      },
    },

    /* ── Tag: a filed label — mono, uppercase, 2px ── */
    Tag: {
      baseStyle: {
        container: {
          fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          borderRadius: "2px",
          fontWeight: "500",
          fontSize: "11.5px",
          letterSpacing: "0.07em",
          textTransform: "uppercase",
        },
      },
      variants: {
        subtle: (props: any) => {
          const colorMap: Record<string, { bg: string; color: string }> = {
            green: { bg: "#eae4d9", color: "#275c3b" },
            red: { bg: "#f8efe9", color: "#933016" },
            purple: { bg: "#fbf0ea", color: "#b8451f" },
            blue: { bg: "#eae4d9", color: "#2d4a5e" },
            orange: { bg: "#fbf0ea", color: "#b8451f" },
            gray: { bg: "#e2dbcd", color: "#55504a" },
          };
          const colors = colorMap[props.colorScheme] || { bg: "#e2dbcd", color: "#55504a" };
          return { container: { bg: colors.bg, color: colors.color } };
        },
        solid: (props: any) => {
          const colorMap: Record<string, { bg: string; color: string }> = {
            green: { bg: "#2f6b46", color: "#f7f3ec" },
            red: { bg: "#b23a1b", color: "#f7f3ec" },
            purple: { bg: "#b8451f", color: "#f7f3ec" },
            blue: { bg: "#35566e", color: "#f7f3ec" },
            orange: { bg: "#b8451f", color: "#f7f3ec" },
          };
          const colors = colorMap[props.colorScheme] || { bg: "#b8451f", color: "#f7f3ec" };
          return { container: { bg: colors.bg, color: colors.color } };
        },
        outline: {
          container: {
            borderColor: "#d5ccbc",
            color: "#55504a",
          },
        },
      },
    },

    /* ── Avatar: genuinely round ── */
    Avatar: {
      baseStyle: {
        container: {
          borderRadius: "999px",
        },
        excessLabel: {
          bg: "#e2dbcd",
          color: "#55504a",
          borderRadius: "999px",
        },
      },
      sizes: {
        xs: { container: { w: "24px", h: "24px" }, label: { fontSize: "10px" } },
        sm: { container: { w: "32px", h: "32px" }, label: { fontSize: "12px" } },
        md: { container: { w: "40px", h: "40px" }, label: { fontSize: "14px" } },
        lg: { container: { w: "56px", h: "56px" }, label: { fontSize: "20px" } },
        xl: { container: { w: "80px", h: "80px" }, label: { fontSize: "28px" } },
      },
    },

    /* ── Breadcrumb ── */
    Breadcrumb: {
      baseStyle: {
        link: {
          fontSize: "0.875rem",
          color: "#55504a",
          _hover: {
            color: "#2f2b27",
            textDecoration: "none",
          },
        },
        separator: {
          color: "#7d7669",
        },
      },
    },
  },

  /* ── Global Styles ── */
  styles: {
    global: (props: any) => ({
      body: {
        /* transparent so the dot-grid ground (index.css body::before) shows */
        bg: "transparent",
        color: props.colorMode === "dark" ? "#eae4d9" : "#3a352f",
        fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif",
        fontSize: "1rem",
        lineHeight: "1.62",
        letterSpacing: "0",
      },
      "*, *::before, *::after": {
        borderColor: props.colorMode === "dark" ? "#38332e" : "#d5ccbc",
      },
      a: {
        color: "#b8451f",
        _hover: {
          color: props.colorMode === "dark" ? "#c74e27" : "#9a3818",
        },
      },
    }),
  },
});

TagManager.initialize(tagManagerArgs)

// Initialize i18n
initializeI18n();

const ToastInit = (): null => {
  const toast = useToast();
  React.useEffect(() => {
    initToast(toast);
  }, [toast]);
  return null;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <ChakraProvider theme={theme}>
      <ToastInit />
      <AppConfigProvider>
        <App />
      </AppConfigProvider>
    </ChakraProvider>
);
